<template>
    <div class="min-h-screen bg-[#F4F7FB]">
        <transition
            enter-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="mobileOpen"
                class="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
                @click="mobileOpen = false"
            ></div>
        </transition>

        <aside
            :class="[
                'fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden bg-[#071226] text-white transition-all duration-300 ease-out',
                sidebarCollapsed ? 'lg:w-16' : 'lg:w-72',
                mobileOpen ? 'w-72 translate-x-0' : 'w-72 -translate-x-full lg:translate-x-0',
            ]"
        >
            <div class="pointer-events-none absolute inset-0 overflow-hidden">
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.18),transparent_30%)]"></div>
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(249,115,22,0.12),transparent_30%)]"></div>
                <div class="absolute -left-10 top-0 h-40 w-40 rounded-full bg-orange-500/20 blur-3xl"></div>
                <div class="absolute bottom-20 right-0 h-48 w-48 rounded-full bg-blue-500/20 blur-3xl"></div>
            </div>

            <div
                :class="[
                    'relative flex h-16 shrink-0 items-center border-b border-white/10',
                    sidebarCollapsed ? 'lg:justify-center lg:px-0 px-4' : 'justify-between px-4',
                ]"
            >
                <a
                    :href="route('home')"
                    :class="[
                        'flex items-center overflow-hidden',
                        sidebarCollapsed && !mobileOpen ? 'lg:justify-center' : 'gap-3',
                    ]"
                >
                    <img
                        src="/imgs/tecno/logo_border_blanco.png"
                        alt="Descargas"
                        :class="sidebarCollapsed && !mobileOpen ? 'h-8 w-8 object-contain' : 'h-8 w-auto'"
                    />
                    <div
                        v-show="!sidebarCollapsed || mobileOpen"
                        :class="sidebarCollapsed && !mobileOpen ? 'lg:hidden' : ''"
                    >
                        <!-- <p class="text-sm font-black leading-none tracking-tight">Descargas</p>
                        <p class="mt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-orange-300/90">Interno</p> -->
                    </div>
                </a>

                <button
                    type="button"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/15 text-slate-300 transition hover:border-orange-400/50 hover:text-orange-300 lg:hidden"
                    @click="mobileOpen = false"
                >
                    <XIcon class="h-5 w-5" />
                </button>
            </div>

            <nav
                :class="[
                    'ts-sidebar-scroll relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-4',
                    sidebarCollapsed && !mobileOpen ? 'lg:px-2 px-3' : 'px-3',
                ]"
            >
                <p
                    v-show="!sidebarCollapsed || mobileOpen"
                    class="mb-2 px-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500"
                    :class="sidebarCollapsed && !mobileOpen ? 'lg:hidden' : ''"
                >
                    Menú
                </p>

                <a
                    v-for="item in menuItems"
                    :key="item.name"
                    :href="item.href"
                    :class="[
                        'group mb-1 flex items-center rounded-xl py-2.5 transition',
                        sidebarCollapsed && !mobileOpen ? 'lg:justify-center lg:px-0 px-2.5 gap-3' : 'gap-3 px-2.5',
                        item.active
                            ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                            : 'text-slate-300 hover:bg-white/5 hover:text-white',
                    ]"
                    :title="item.name"
                    @click="mobileOpen = false"
                >
                    <span
                        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                        :class="item.active ? 'bg-white/15' : 'bg-white/5 group-hover:bg-orange-500/20'"
                    >
                        <component :is="item.icon" class="h-4 w-4" />
                    </span>
                    <span
                        class="truncate text-sm font-semibold"
                        :class="sidebarCollapsed && !mobileOpen ? 'lg:hidden' : ''"
                    >
                        {{ item.name }}
                    </span>
                </a>
            </nav>

            <div
                :class="[
                    'relative shrink-0 border-t border-white/10',
                    sidebarCollapsed && !mobileOpen ? 'lg:p-2 p-3' : 'p-3',
                ]"
            >
                <button
                    type="button"
                    :class="[
                        'mb-3 hidden items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xs font-semibold text-slate-300 transition hover:border-orange-400/40 hover:text-orange-300 lg:flex',
                        sidebarCollapsed ? 'h-9 w-full px-0' : 'w-full gap-2 px-2 py-2',
                    ]"
                    @click="toggleSidebar"
                    :title="sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'"
                >
                    <ChevronDoubleLeftIcon
                        :class="['h-4 w-4 transition-transform duration-300', sidebarCollapsed ? 'rotate-180' : '']"
                    />
                    <span v-if="!sidebarCollapsed">Contraer</span>
                </button>

                <div
                    :class="[
                        'rounded-xl border border-white/10 bg-white/5',
                        sidebarCollapsed && !mobileOpen ? 'lg:p-1.5 p-2.5' : 'p-2.5',
                    ]"
                >
                    <div
                        :class="[
                            'flex items-center',
                            sidebarCollapsed && !mobileOpen ? 'lg:justify-center gap-0' : 'gap-2.5',
                        ]"
                    >
                        <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-sm font-bold text-white shadow-md shadow-orange-500/25">
                            {{ userInitial }}
                        </span>
                        <div
                            class="min-w-0 flex-1"
                            :class="sidebarCollapsed && !mobileOpen ? 'lg:hidden' : ''"
                        >
                            <p class="truncate text-sm font-bold text-white">{{ this.$page.props.user.name }}</p>
                            <p class="truncate text-[11px] text-slate-400">Sesión activa</p>
                        </div>
                    </div>

                    <a
                        :href="route('logoutg')"
                        class="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/5 px-2 py-2 text-[11px] font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white"
                        title="Salir"
                    >
                        <LogoutIcon class="h-3.5 w-3.5" />
                        <span :class="sidebarCollapsed && !mobileOpen ? 'lg:hidden' : ''">Salir</span>
                    </a>
                </div>
            </div>
        </aside>

        <div
            :class="[
                'min-h-screen transition-all duration-300 ease-out',
                sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72',
            ]"
        >
            <header class="sticky top-0 z-30 border-b border-slate-200 bg-white shadow-sm">
                <div class="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
                    <div class="flex items-center gap-3">
                        <button
                            type="button"
                            class="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-base-primary shadow-sm transition hover:border-orange-300 hover:text-orange-600 lg:hidden"
                            @click="mobileOpen = true"
                        >
                            <MenuIcon class="h-5 w-5" />
                        </button>
                        <div>
                            <p class="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-600">Descargas</p>
                            <p class="text-sm font-bold text-base-primary">{{ pageHeading }}</p>
                        </div>
                    </div>

                    <div class="relative" ref="userMenu">
                        <button
                            type="button"
                            class="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-600"
                            @click="userMenuOpen = !userMenuOpen"
                        >
                            <span class="hidden sm:inline">{{ this.$page.props.user.name }}</span>
                            <span class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-base-primary text-xs font-bold text-white">
                                {{ userInitial }}
                            </span>
                            <ChevronDownIcon class="h-4 w-4 text-slate-400" />
                        </button>

                        <div
                            v-if="userMenuOpen"
                            class="absolute right-0 z-40 mt-2 w-48 origin-top-right rounded-2xl border border-slate-100 bg-white py-2 shadow-xl"
                        >
                            <p class="px-4 pb-2 pt-1 text-xs font-semibold uppercase tracking-wide text-slate-400 sm:hidden">
                                {{ this.$page.props.user.name }}
                            </p>
                            <a
                                :href="route('logoutg')"
                                class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-orange-50 hover:text-orange-600"
                            >
                                <LogoutIcon class="h-4 w-4" />
                                Salir
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            <main class="animate-fade-in-up">
                <slot />
            </main>
        </div>
    </div>
</template>

<script>
import {
    HomeIcon,
    UserGroupIcon,
    LogoutIcon,
    MenuIcon,
    XIcon,
    ChevronDoubleLeftIcon,
    ChevronDownIcon,
} from '@heroicons/vue/outline';

const SIDEBAR_KEY = 'descargas_sidebar_collapsed';

export default {
    components: {
        HomeIcon,
        UserGroupIcon,
        LogoutIcon,
        MenuIcon,
        XIcon,
        ChevronDoubleLeftIcon,
        ChevronDownIcon,
    },
    data() {
        let collapsed = false;
        try {
            collapsed = localStorage.getItem(SIDEBAR_KEY) === '1';
        } catch (e) {}

        return {
            sidebarCollapsed: collapsed,
            mobileOpen: false,
            userMenuOpen: false,
        };
    },
    computed: {
        userInitial() {
            const name = this.$page.props.user?.name || 'U';
            return name.trim().charAt(0).toUpperCase();
        },
        pageHeading() {
            const url = this.$page?.url || '';
            if (url.includes('engineers')) return 'Ingenieros';
            return 'Inicio';
        },
        menuItems() {
            const url = this.$page?.url || '';
            const items = [
                {
                    name: 'Inicio',
                    href: this.route('home'),
                    icon: 'HomeIcon',
                    active: url === '/' || url === '' || (!url.includes('engineers') && !url.includes('login')),
                },
            ];

            if (this.$page.props.access?.access_level_id == 3) {
                items.push({
                    name: 'Ingenieros',
                    href: this.route('engineers'),
                    icon: 'UserGroupIcon',
                    active: url.includes('engineers'),
                });
            }

            return items;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        toggleSidebar() {
            this.sidebarCollapsed = !this.sidebarCollapsed;
            try {
                localStorage.setItem(SIDEBAR_KEY, this.sidebarCollapsed ? '1' : '0');
            } catch (e) {}
        },
        handleDocumentClick(event) {
            const menu = this.$refs.userMenu;
            if (menu && !menu.contains(event.target)) {
                this.userMenuOpen = false;
            }
        },
    },
};
</script>
